/**
 * The space a chat is filed in, on its Chat sidebar row.
 *
 * Ported from pingdotgg/t3code (MIT): upstream marks the project a thread
 * belongs to with a 14px favicon (`ProjectFavicon`, `size-3.5`) leading the
 * project's name. Amoreg's list is flat, so the mark rides each row instead:
 * it leads the card's "Amoreg Eve" line at the same 14px, and it takes the
 * settled row's 16px leading icon slot. The space's name is in the tooltip.
 * A chat in no space keeps its row as it was (`fallback`).
 *
 * @module components/sidebar/SidebarThreadSpaceMark
 */
import type { ReactNode } from "react";

import type { Space } from "../../state/spaces";
import { SpaceGlyph } from "../spaces/SpaceGlyph";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";

/** Glyph sizes per row: the card's text-xs line, the settled row's icon slot. */
const GLYPH_CLASS = {
  // The text size drops the glyph's own leading in the class merge; restate it.
  card: "size-3.5 rounded-[3.5px] text-[8px] leading-none",
  slim: "size-4 rounded-[4px] text-[9px] leading-none",
} as const;

export function SidebarThreadSpaceMark({
  space,
  variant,
  fallback = null,
}: {
  readonly space: Space | null;
  readonly variant: keyof typeof GLYPH_CLASS;
  /** What the row shows for a chat in no space. */
  readonly fallback?: ReactNode;
}) {
  if (space === null) return fallback;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            role="img"
            aria-label={`Space: ${space.name}`}
            data-thread-space-mark={space.id}
            className="inline-flex shrink-0"
          />
        }
      >
        <SpaceGlyph space={space} className={GLYPH_CLASS[variant]} />
      </TooltipTrigger>
      <TooltipPopup side="top">{space.name}</TooltipPopup>
    </Tooltip>
  );
}
